import { useMemo, useRef } from 'react';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useToast } from '@cbhq/cds-web/overlays/useToast';

import { handleCsrError } from ':support/utils/notifyError';

type QueryProviderProps = {
  children: React.ReactNode;
};

export function QueryProvider({ children }: QueryProviderProps) {
  const toast = useToast();
  const toastRef = useRef(toast);

  // Keep toast reference updated
  toastRef.current = toast;

  const queryClient = useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          retry: false,
          throwOnError: () => false,
        },
      },
      queryCache: new QueryCache({
        onError: (error, { meta }): void => {
          // Log error if errorMsg is provided (with toast) or logError flag is set (without toast)
          if (meta?.errorMsg || meta?.logError) {
            handleCsrError(error);
          }

          // Only show toast if errorMsg is provided
          if (meta?.errorMsg) {
            toastRef.current.show(meta.errorMsg as string, { variant: 'bgNegative' });
          }
        },
      }),
    });
  }, []); // No dependencies - QueryClient created only once

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
